import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ProjectsContext = createContext(null);

function cacheKey(userId) { return userId ? `bf_projects_${userId}` : null; }

export function ProjectsProvider({ children }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const persist = useCallback((next) => {
    if (!user) return;
    try { localStorage.setItem(cacheKey(user.id), JSON.stringify(next)); } catch { /* ignore */ }
  }, [user]);

  const refresh = useCallback(async (archived = includeArchived) => {
    try {
      const { data } = await api.get(`/projects${archived ? "?include_archived=true" : ""}`);
      setProjects(data);
      setLoaded(true);
      persist(data);
      return data;
    } catch {
      return null;
    }
  }, [includeArchived, persist]);

  // Hydrate from cache first, then fetch fresh.
  useEffect(() => {
    if (!user) { setProjects([]); setLoaded(false); return; }
    const k = cacheKey(user.id);
    try {
      const raw = localStorage.getItem(k);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setProjects(parsed);
      }
    } catch { /* ignore */ }
    refresh(false);
  }, [user, refresh]);

  const touch = useCallback(async (id) => {
    try {
      const { data } = await api.post(`/projects/${id}/touch`);
      setProjects((arr) => {
        const next = arr.map((p) => p.id === id ? { ...p, ...data } : p);
        // Re-sort: pinned first, then last_opened_at desc
        next.sort((a, b) => {
          if (!!b.pinned - !!a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
          return (b.last_opened_at || "").localeCompare(a.last_opened_at || "");
        });
        persist(next);
        return next;
      });
    } catch { /* ignore */ }
  }, [persist]);

  const togglePin = useCallback(async (id) => {
    const { data } = await api.post(`/projects/${id}/pin`);
    setProjects((arr) => {
      const next = arr.map((p) => p.id === id ? { ...p, ...data } : p);
      next.sort((a, b) => {
        if (!!b.pinned - !!a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
        return (b.last_opened_at || "").localeCompare(a.last_opened_at || "");
      });
      persist(next);
      return next;
    });
    return data;
  }, [persist]);

  const toggleArchive = useCallback(async (id) => {
    const { data } = await api.post(`/projects/${id}/archive`);
    setProjects((arr) => {
      let next = arr.map((p) => p.id === id ? { ...p, ...data } : p);
      if (!includeArchived && data.archived) next = next.filter((p) => p.id !== id);
      persist(next);
      return next;
    });
    return data;
  }, [includeArchived, persist]);

  const duplicate = useCallback(async (id) => {
    const { data } = await api.post(`/projects/${id}/duplicate`);
    setProjects((arr) => {
      const next = [data, ...arr];
      persist(next);
      return next;
    });
    return data;
  }, [persist]);

  const remove = useCallback(async (id) => {
    await api.delete(`/projects/${id}`);
    setProjects((arr) => {
      const next = arr.filter((p) => p.id !== id);
      persist(next);
      return next;
    });
  }, [persist]);

  const create = useCallback(async (payload) => {
    const { data } = await api.post("/projects", payload);
    setProjects((arr) => {
      const next = [data, ...arr];
      persist(next);
      return next;
    });
    return data;
  }, [persist]);

  const updateOne = useCallback((id, patch) => {
    setProjects((arr) => {
      const next = arr.map((p) => p.id === id ? { ...p, ...patch } : p);
      persist(next);
      return next;
    });
  }, [persist]);

  const value = useMemo(() => ({
    projects,
    loaded,
    includeArchived,
    setIncludeArchived,
    refresh,
    touch,
    togglePin,
    toggleArchive,
    duplicate,
    remove,
    create,
    updateOne,
  }), [projects, loaded, includeArchived, refresh, touch, togglePin, toggleArchive, duplicate, remove, create, updateOne]);

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
}

export function useProjects() {
  const ctx = useContext(ProjectsContext);
  if (!ctx) throw new Error("useProjects must be used within ProjectsProvider");
  return ctx;
}
